import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Alert, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { distributeAliquotsEqually } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";

/**
 * HO-HU-36: aviso del estado de las alicuotas del condominio, con la accion
 * para arreglarlo.
 *
 * Existe porque hay un estado silencioso que descoloca todo el cobro: una
 * unidad sin fila en `aliquots` queda FUERA del reparto de gastos (el query del
 * reparto hace INNER JOIN con esa tabla), asi que no se le cobra nada y en
 * ningun lado se avisaba. Es justo lo que deja el import de propietarios por
 * Excel, que es el primer paso de la guia.
 *
 * No muestra nada cuando las alicuotas estan sanas: es un aviso, no un panel.
 */
const AliquotHealthAlert = ({ aliquot, onDistributed, className }) => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const [distributing, setDistributing] = useState(false);

  if (!aliquot || aliquot.properties === 0) {
    return null;
  }

  const missing = aliquot.withoutAliquot > 0;
  const doesNotAddUp = !missing && Math.abs(Number(aliquot.sum) - 100) > 0.01;

  if (!missing && !doesNotAddUp) {
    return null;
  }

  const sumLabel = Number(aliquot.sum).toLocaleString("es-VE", { maximumFractionDigits: 4 });

  const handleDistribute = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Distribuir en partes iguales?",
      html: `Cada uno de los <strong>${aliquot.properties}</strong> inmuebles va a quedar con una alícuota de <strong>${(
        100 / aliquot.properties
      ).toFixed(4)}%</strong>.<br/><br/>Si ya habías cargado alícuotas distintas para cada inmueble, <strong>se van a reemplazar</strong>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, distribuir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#950ec3",
    });

    if (!isConfirmed) {
      return;
    }

    setDistributing(true);
    try {
      await distributeAliquotsEqually(selectedCondominium);
      Toast.fire({
        type: "success",
        title: "Alícuotas distribuidas en partes iguales.",
      });
      if (onDistributed) await onDistributed();
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setDistributing(false);
    }
  };

  return (
    <Alert color="warning" className={`fs--1 mb-0 ${className || ""}`}>
      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
      {missing ? (
        <>
          {aliquot.withoutAliquot === 1
            ? "1 inmueble no tiene alícuota"
            : `${aliquot.withoutAliquot} inmuebles no tienen alícuota`}{" "}
          y por ahora quedan fuera del reparto de gastos: no se les va a cobrar
          nada hasta que la tengan. Pasa con los inmuebles que se crean al
          importar propietarios desde Excel.
        </>
      ) : (
        <>
          Las alícuotas suman {sumLabel}% y no 100%. Si así lo establece el
          documento del condominio, puedes dejarlas: cada propietario paga su
          alícuota de cada gasto tal cual, así que lo que se cobra se aparta de
          los gastos en esa proporción.
        </>
      )}
      {missing && (
      <div className="mt-2">
        <Button
          color="warning"
          size="sm"
          disabled={distributing}
          onClick={handleDistribute}
        >
          {distributing ? "Distribuyendo..." : "Distribuir equitativamente"}
        </Button>
      </div>
      )}
    </Alert>
  );
};

AliquotHealthAlert.propTypes = {
  /** Bloque `aliquot` del estado de onboarding. */
  aliquot: PropTypes.shape({
    properties: PropTypes.number,
    withoutAliquot: PropTypes.number,
    sum: PropTypes.number,
    isHealthy: PropTypes.bool,
  }),
  /** Se llama despues de distribuir, para refrescar lo que muestre el padre. */
  onDistributed: PropTypes.func,
  className: PropTypes.string,
};

export default AliquotHealthAlert;
